import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_NAMES = {
  seniors_program: "Complete UNSTUCK Seniors Program",
  seniors_deck: "Individual UNSTUCK Seniors Deck",
  extra_bucks: "Extra UNSTUCK Bucks (digital printable PDF)",
};

const DOWNLOAD_COPY = {
  seniors_program: {
    title: "Your digital support materials",
    body: "The Full Seniors Program includes four digital PDF files, available now: the instructions PDF, the printable UNSTUCK Bucks PDF, the Participation & Progress Tracker, and the Understanding Your Tracker guide. The physical activity cards themselves are not downloadable PDFs — they ship to you. A confirmation email with these download links is on its way to your inbox.",
    links: [
      { key: "instructions", label: "Instructions PDF" },
      { key: "bucks", label: "Printable UNSTUCK Bucks PDF" },
      { key: "tracker", label: "Participation Tracker PDF" },
      { key: "tracker_guide", label: "Tracker Guide PDF" },
    ],
  },
  extra_bucks: {
    title: "Your digital download",
    body: "Print as many Bucks as you need for personal or internal program use. No resale or redistribution. GAME USE ONLY • NO CASH VALUE. A confirmation email with this download link is on its way to your inbox.",
    links: [{ key: "bucks", label: "Printable UNSTUCK Bucks PDF" }],
  },
};

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError("No order session found.");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          return;
        }
        attempts += 1;
        if (attempts >= 10) {
          setError("Your payment is still being confirmed. Please check back shortly or contact us.");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setError("We could not confirm your order. If you completed payment, please contact us.");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="bg-cream">
      <Seo title="Order Confirmed" noindex />
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-20 sm:py-28 text-center">
        {!order && !error && (
          <div data-testid="payment-confirming">
            <Loader2 className="h-12 w-12 animate-spin text-sage mx-auto mb-6" aria-hidden="true" />
            <h1 className="font-heading font-bold text-3xl text-ink mb-3">Confirming your order…</h1>
            <p className="text-lg text-ink/70">This takes just a moment.</p>
          </div>
        )}

        {error && (
          <div data-testid="payment-error">
            <h1 className="font-heading font-bold text-3xl text-ink mb-4">Almost there</h1>
            <p className="text-lg text-ink/75 mb-8">{error}</p>
            <Link to="/contact" data-testid="payment-error-contact-link" className="inline-flex items-center justify-center bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 text-lg font-medium transition-all duration-300">
              Contact Us
            </Link>
          </div>
        )}

        {order && (
          <div data-testid="payment-confirmed">
            <div className="h-20 w-20 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="h-10 w-10 text-sage" aria-hidden="true" />
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-ink mb-4">
              Thank you — your order is confirmed.
            </h1>
            <p className="text-lg sm:text-xl text-ink/80 mb-10">
              {PRODUCT_NAMES[order.lookup_key] || "Your UNSTUCK order"} is confirmed.
              {order.lookup_key !== "extra_bucks"
                ? " Your physical items will ship to the address you entered at checkout."
                : " It is a digital product — nothing ships."}
            </p>

            {DOWNLOAD_COPY[order.lookup_key] && (
              <div className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10 text-left mb-10" data-testid="digital-downloads">
                <h2 className="font-heading font-bold text-2xl text-ink mb-3">{DOWNLOAD_COPY[order.lookup_key].title}</h2>
                <p className="text-base text-ink/75 mb-6">{DOWNLOAD_COPY[order.lookup_key].body}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {DOWNLOAD_COPY[order.lookup_key].links.map((link) => (
                    <a
                      key={link.key}
                      href={`${API}/downloads/${sessionId}/${link.key}`}
                      data-testid={`download-${link.key}-link`}
                      className="inline-flex items-center justify-center gap-2 bg-sage text-white hover:bg-sage-dark rounded-full px-6 py-4 text-lg font-medium transition-all duration-300"
                    >
                      <Download className="h-5 w-5" aria-hidden="true" /> {link.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <Link
              to="/"
              data-testid="payment-success-home-link"
              className="inline-flex items-center justify-center border-2 border-sage text-sage hover:bg-sage hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
            >
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
